import { baseApi } from '@/shared/api/baseApi';

import {
  formFullInfoSchema,
  formListSchema,
  type FormFullInfo,
  type FormList,
} from './forms.schema';

interface FormArgs {
  asicCompanyId: string;
  formId: string;
}

interface FormListArgs {
  asicCompanyId: string;
  page?: number;
  perPage?: number;
}

interface ChangeArgs {
  asicCompanyId: string;
  changeId: string;
}

interface SignDocumentArgs {
  documentId: string;
  /** Base64 signature image, as produced by the signature pad. */
  signature: string;
}

interface DeleteDocumentArgs {
  asicCompanyId: string;
  documentId: string;
}

/**
 * Form, change and signing endpoints.
 *
 * Signing invalidates broadly on purpose. A signature changes the form, the
 * annual review that owns it, and the outstanding-items count in the header;
 * listing all three here is what keeps those in step without a single manual
 * refetch at any call site.
 */
export const formsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Every change lodged for one company. Tagged per company so opening a
     * second company does not evict the first.
     *
     * Only `page` and `perPage` are sent, which is exactly what the legacy
     * client sent (`compileFilters` in its Forms tab). An earlier version here
     * also sent `currentPage`/`itemsPerPage` alongside them, plus empty
     * `searchBy`, `type`, `status` and `date[...]` values — the duplicates were
     * never a real API contract, and empty filters are not the same request as
     * no filters. The tab sends no filters at all, so neither does this.
     *
     * This endpoint does honour `order[columnName]`/`order[direction]`, and is
     * the only one in the API that does — everywhere else a sort has to happen
     * in the browser over the fetched page. The arguments for it are not kept
     * here on spec: the Changes tab is a list, not a query surface, so nothing
     * would pass them. Restoring sorting means adding them back alongside the
     * control that sends them.
     */
    getForms: build.query<FormList, FormListArgs>({
      query: ({ asicCompanyId, page = 1, perPage = 10 }) => ({
        url: `/director/${asicCompanyId}/forms`,
        params: { page, perPage },
      }),
      transformResponse: (response: unknown): FormList => formListSchema.parse(response),
      providesTags: (_result, _error, { asicCompanyId }) => [
        { type: 'Form', id: asicCompanyId },
      ],
    }),

    getForm: build.query<FormFullInfo, FormArgs>({
      query: ({ asicCompanyId, formId }) => `/director/${asicCompanyId}/form/${formId}`,
      transformResponse: (response: unknown): FormFullInfo =>
        formFullInfoSchema.parse(response),
      providesTags: (_result, _error, { formId }) => [{ type: 'Form', id: formId }],
    }),

    /** The same payload reached by the change that produced the form — the
     *  route a director follows from a "company details changed" email. */
    getFormByChange: build.query<FormFullInfo, ChangeArgs>({
      query: ({ asicCompanyId, changeId }) =>
        `/director/${asicCompanyId}/form/change/${changeId}`,
      transformResponse: (response: unknown): FormFullInfo =>
        formFullInfoSchema.parse(response),
      providesTags: (_result, _error, { changeId }) => [{ type: 'Form', id: changeId }],
    }),

    signDocument: build.mutation<unknown, SignDocumentArgs>({
      query: ({ documentId, signature }) => ({
        url: `/director/sign/document/${documentId}`,
        method: 'PUT',
        body: { signature },
      }),
      invalidatesTags: ['Form', 'AnnualReview', 'Document'],
    }),

    /** Signs every outstanding document in one action. */
    signAllDocuments: build.mutation<unknown, { signature: string }>({
      query: (body) => ({ url: '/director/signAll', method: 'PUT', body }),
      invalidatesTags: ['Form', 'AnnualReview', 'Document'],
    }),

    deleteDocument: build.mutation<undefined, DeleteDocumentArgs>({
      query: ({ asicCompanyId, documentId }) => ({
        url: `/director/${asicCompanyId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Document', 'Form'],
    }),
  }),
});

export const {
  useGetFormsQuery,
  useGetFormQuery,
  useGetFormByChangeQuery,
  useSignDocumentMutation,
  useSignAllDocumentsMutation,
  useDeleteDocumentMutation,
} = formsApi;
